// @flow

import * as React from "react";

import Emojify from "app/components/shared/Emojify";
import FriendlyTime from "app/components/shared/FriendlyTime";
import PipelineStateIcon from "app/components/shared/PipelineStateIcon";
import RemoteButtonComponent from "app/components/shared/RemoteButtonComponent";
import UserAvatar, {
  type JobPresenterUser,
} from "app/components/shared/UserAvatar";

import Database from "app/lib/Database";

import BlockFieldsModal, {
  type Field,
} from "app/components/job/BlockFieldsModal";

export type ManualStepType = {
  type: "manual",
  id: string,
  name: ?string,
  state: string,
  unblockPath: string,
  prompt: ?string,
  submit: ?string,
  fields: Array<Field>,
  unblockedAt?: string,
  unblockedBy: ?JobPresenterUser,
  permissions: {
    activate: {
      allowed: boolean,
    },
  },
  stepUuid: string,
};

type Props = {
  job: ManualStepType,
  buildStore: {
    loadAndEmit: (any) => void,
    reload: () => void,
  },
};

type State = {
  fieldsModalActivated: boolean,
};

export default class ManualJobSummary extends React.PureComponent<
  Props,
  State,
> {
  state = {
    fieldsModalActivated: false,
  };

  fieldsModal: ?BlockFieldsModal;

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      this.fieldsModal &&
      prevState.fieldsModalActivated !== this.state.fieldsModalActivated
    ) {
      this.fieldsModal.show();
    }
  }

  renderSummary() {
    if (this.props.job.unblockedAt && this.props.job.unblockedBy) {
      return (
        <div className="build-details-pipeline-manual-job">
          <span className="unblocked">
            <UserAvatar
              user={UserAvatar.convertUserFromJobPresenter(
                this.props.job.unblockedBy,
              )}
              style={{ width: 24, height: 24, marginRight: 6 }}
              suppressAltText={true}
            />
            <strong className="semi-bold">
              {this.props.job.unblockedBy && this.props.job.unblockedBy.name}
            </strong>
            {" unblocked "}
            <strong className="semi-bold">
              <Emojify text={this.props.job.name || "Continue"} />
            </strong>
            {this.unblockedAtNode()}
          </span>
        </div>
      );
    } else if (!this.props.job.permissions.activate.allowed) {
      return (
        <div className="build-details-pipeline-manual-job">
          <button className="btn btn-default btn--disabled" disabled={true}>
            <div className="flex items-center">
              <PipelineStateIcon job={this.props.job} />
              <strong className="semi-bold ml1">
                <Emojify text={this.props.job.name || "Continue"} />
              </strong>
            </div>
          </button>
        </div>
      );
    } else if (
      this.props.job.state === "skipped" ||
      this.props.job.state === "canceled" ||
      this.props.job.state === "blocked_failed"
    ) {
      return (
        <div className="build-details-pipeline-manual-job">
          <button
            href={this.props.job.unblockPath}
            className="btn btn-default btn--disabled"
            disabled={true}
          >
            <div className="flex items-center">
              <PipelineStateIcon job={this.props.job} />
              <strong className="semi-bold ml1">
                <Emojify text={this.props.job.name || "Continue"} />
              </strong>
            </div>
          </button>
        </div>
      );
    }

    if (this.props.job.fields) {
      return (
        <div className="build-details-pipeline-manual-job">
          <a
            href="#"
            className="btn btn-default"
            onClick={this.handleContinueButtonClick}
          >
            <div className="flex items-center">
              <PipelineStateIcon job={this.props.job} />
              <strong className="semi-bold ml1">
                <Emojify text={this.props.job.name || "Continue"} />
              </strong>
            </div>
          </a>
        </div>
      );
    }

    return (
      <div className="build-details-pipeline-manual-job">
        <RemoteButtonComponent
          url={this.props.job.unblockPath}
          method="post"
          confirmText={this.props.job.prompt || "Unblock this step?"}
          className="btn btn-default"
          onSuccess={(_event, response) => {
            if (response?.build) {
              this.props.buildStore.loadAndEmit(Database.parse(response.build));
            }
          }}
          onError={(_event, response) => {
            if (response?.message) {
              alert(response.message);
            }
            this.props.buildStore.reload();
          }}
        >
          <div className="flex items-center">
            <PipelineStateIcon job={this.props.job} />
            <strong className="semi-bold ml1">
              <Emojify text={this.props.job.name || "Continue"} />
            </strong>
          </div>
        </RemoteButtonComponent>
      </div>
    );
  }

  handleContinueButtonClick = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    if (this.state.fieldsModalActivated) {
      if (this.fieldsModal) {
        this.fieldsModal.show();
      }
    } else {
      this.setState({
        fieldsModalActivated: true,
      });
    }
  };

  unblockedAtNode() {
    if (this.props.job.unblockedAt) {
      return (
        <small className="text-muted">
          <br />{" "}
          <FriendlyTime value={this.props.job.unblockedAt} capitalized={true} />
        </small>
      );
    }
  }

  render() {
    const fieldsModal = this.state.fieldsModalActivated && (
      <BlockFieldsModal
        ref={(fieldsModal) => (this.fieldsModal = fieldsModal)}
        buildStore={this.props.buildStore}
        job={this.props.job}
      />
    );

    return (
      <div>
        {fieldsModal}
        {this.renderSummary()}
      </div>
    );
  }
}
